import { useMemo } from 'react'
import ViolationRow from './ViolationRow'
import { composeGroupVerdict } from '@/lib/severity-group-verdict'
import { Accordion, type AccordionItem } from '@/components/carbon/Accordion'
import { IconCheck } from '@/components/carbon/icons'
import { SeverityGlyph } from '@/components/carbon/SeverityGlyph'
import { Tag } from '@/components/carbon/Tag'
import type { SeverityLevel } from '@/components/carbon/types'
import type { ComplianceViolation } from '@/types'

type GroupKey = 'critical' | 'high' | 'medium' | 'passing'

interface GroupedPoliciesDisplayProps {
  policies: ComplianceViolation[]
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
  frameworkCode?: string  // For contextual framework reference display
  /** When set, that group renders open on initial mount (deep-link entry). */
  initialOpenKey?: GroupKey
}

interface PolicyGroup {
  key: GroupKey
  policies: ComplianceViolation[]
  defaultOpen: boolean
}

// Group headers say the severity once, as glyph plus word (DESIGN.md
// "Severity"); the SHALL/SHOULD/MAY vocabulary lives in the tooltip and the
// rows' Frameworks column. Passing is neutral, with a check (D12).
const GROUP_META: Record<GroupKey, { label: string; level?: SeverityLevel; tooltip: string }> = {
  critical: { label: 'Critical', level: 'crit', tooltip: 'Critical controls. Must-fix per the frameworks.' },
  high: { label: 'High', level: 'high', tooltip: 'High-priority controls. Recommended by the frameworks.' },
  medium: { label: 'Medium', level: 'med', tooltip: 'Medium-priority controls. Optional or aspirational.' },
  passing: { label: 'Passing', tooltip: 'Controls currently in place.' },
}

/**
 * Sort comparator for policies within a criticality bucket.
 *
 * Primary: Guardian Score impact, descending — the biggest score movers float
 * to the top of each group. Null/undefined deltas (passing policies, or
 * findings with no computed lift) sort last so the actionable rows lead.
 *
 * Secondary: policy code in natural order — the workload segment compares
 * alphabetically and the trailing number numerically, so a bucket reads
 * GUARD.IAM.001, GUARD.IAM.002, GUARD.INTUNE.001, GUARD.INTUNE.002… making a
 * given policy easy to find. `numeric: true` also handles any non-padded codes.
 */
function comparePolicies(a: ComplianceViolation, b: ComplianceViolation): number {
  const deltaA = a.guardianScoreDelta ?? Number.NEGATIVE_INFINITY
  const deltaB = b.guardianScoreDelta ?? Number.NEGATIVE_INFINITY
  if (deltaA !== deltaB) return deltaB - deltaA
  return a.policyCode.localeCompare(b.policyCode, undefined, { numeric: true, sensitivity: 'base' })
}

/**
 * Groups policies by status (Pass) and criticality (SHALL/SHOULD/MAY) into
 * Carbon accordion rows (DESIGN.md "Components"): each group is a header
 * button on layer-01 (severity glyph and word, a count tag, the verdict in
 * helper text, a chevron that turns on open) over its triage rows. Rows split
 * by border-subtle lines; nothing nests. Empty groups are not rendered.
 * Uses a single-pass O(n) loop for grouping instead of multiple filter calls.
 * Within each section, policies are ordered by Guardian Score impact then
 * policy code (see comparePolicies). Every section starts collapsed — an
 * auto-opened band makes the page scroll too long. Only a deep-link
 * (initialOpenKey) opens its bucket.
 *
 * The groups are a Carbon Accordion: `lazy`, so a group's rows render only
 * while it is open and a long framework list stays cheap, and `flush`, so the
 * rows run full width under the header's rule.
 */
export default function GroupedPoliciesDisplay({
  policies,
  onAddWaiver,
  onEditWaiver,
  onDeleteWaiver,
  frameworkCode,
  initialOpenKey,
}: GroupedPoliciesDisplayProps) {
  // Group policies by status (Pass) and criticality (SHALL/SHOULD/MAY)
  // Performance optimization: Single pass through array instead of 4 filter calls
  const groups = useMemo<PolicyGroup[]>(() => {
    const passing: ComplianceViolation[] = []
    const critical: ComplianceViolation[] = []
    const high: ComplianceViolation[] = []
    const medium: ComplianceViolation[] = []

    // Single iteration - O(n) instead of O(4n)
    for (const p of policies) {
      if (p.status === 'Pass') {
        passing.push(p)
      } else {
        switch (p.criticality) {
          case 'SHALL':
            critical.push(p)
            break
          case 'SHOULD':
            high.push(p)
            break
          case 'MAY':
            medium.push(p)
            break
        }
      }
    }

    const buckets: Record<GroupKey, ComplianceViolation[]> = { critical, high, medium, passing }
    // Only a deep-link opens a bucket, and only when it has rows (empty
    // groups aren't rendered). No deep-link = everything collapsed.
    const openKey =
      initialOpenKey && buckets[initialOpenKey].length > 0 ? initialOpenKey : undefined
    return (Object.keys(buckets) as GroupKey[]).map((key) => ({
      key,
      policies: buckets[key].sort(comparePolicies),
      defaultOpen: key === openKey,
    }))
  }, [policies, initialOpenKey])

  const openCriticals =
    groups.find((g) => g.key === 'critical')?.policies.filter((p) => p.status === 'Open').length ?? 0

  // Empty groups are not rendered.
  const items: AccordionItem[] = groups
    .filter((group) => group.policies.length > 0)
    .map((group) => {
      const meta = GROUP_META[group.key]
      const verdict = composeGroupVerdict(group.key, group.policies, { openCriticals })
      return {
        id: group.key,
        title: (
          <span className="flex min-w-0 items-center gap-3">
            <span title={meta.tooltip} className="flex-none">
              {meta.level ? (
                <SeverityGlyph level={meta.level}>{meta.label}</SeverityGlyph>
              ) : (
                <span className="inline-flex items-center gap-2">
                  <IconCheck className="text-[color:var(--icon-secondary)]" />
                  {meta.label}
                </span>
              )}
            </span>
            <Tag size="sm" className="flex-none tabular-nums">
              {group.policies.length}
            </Tag>
          </span>
        ),
        // The verdict is helper text beside the chevron, from c-md up.
        meta: verdict ? <span className="hidden max-w-[48ch] truncate c-md:block">{verdict}</span> : undefined,
        content: group.policies.map((policy) => (
          <ViolationRow
            key={policy.id}
            violation={policy}
            onAddWaiver={group.key !== 'passing' ? onAddWaiver : undefined}
            onEditWaiver={group.key !== 'passing' ? onEditWaiver : undefined}
            onDeleteWaiver={group.key !== 'passing' ? onDeleteWaiver : undefined}
            frameworkCode={frameworkCode}
          />
        )),
      }
    })

  return (
    <Accordion
      items={items}
      defaultOpen={groups.filter((g) => g.defaultOpen).map((g) => g.key)}
      lazy
      flush
      headingLevel="h4"
      className="bg-layer-1"
    />
  )
}
